import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react'
import { ChevronDownIcon, X } from 'lucide-react'

export interface FilterOption {
  key: string
  label: string
}

interface FilterDropdownProps {
  label: string
  selectedKey: string
  options: FilterOption[]
  onSelectionChange: (key: string) => void
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost'
  minWidth?: string
  showClearButton?: boolean
}

/**
 * Reusable filter dropdown component with consistent styling
 */
const FilterDropdown = ({
  label,
  selectedKey,
  options,
  onSelectionChange,
  className = '',
  size = 'lg',
  variant = 'bordered',
  minWidth = 'min-w-[100px]',
  showClearButton = false,
}: FilterDropdownProps) => {
  const selectedOption = options.find(option => option.key === selectedKey)
  const displayLabel = selectedOption?.label || label

  // Default is the first option (usually 'all')
  const defaultKey = options[0]?.key || ''

  // Check if the current selection is different from default
  const hasActiveFilter = showClearButton && selectedKey !== defaultKey

  const handleClear = () => {
    // Reset to first option (usually 'all')
    onSelectionChange(defaultKey)
  }

  return (
    <div className="relative">
      <Dropdown>
        <DropdownTrigger>
          <Button
            className={`${minWidth} ${className}`}
            endContent={<ChevronDownIcon className="text-small flex-shrink-0" />}
            radius="sm"
            size={size}
            variant={variant}
          >
            {displayLabel}
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          selectedKeys={new Set([selectedKey])}
          selectionMode="single"
          onAction={key => onSelectionChange(key as string)}
        >
          {options.map(option => (
            <DropdownItem key={option.key}>{option.label}</DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      {/* Clear button */}
      {hasActiveFilter && (
        <button
          className="absolute -right-2 -top-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
          title="Clear filter"
          type="button"
          onClick={handleClear}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}

export default FilterDropdown
